import { collection, getDocs, doc, getDoc, addDoc, updateDoc, query, where, Timestamp, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { Exam } from '../types';
import { getBuraxilisQuestionsForFenn } from '../utils/buraxilisQuestionUtils';
import {
  BuraxilisBlankContext,
  Grade11BlankScheme,
  blankCtxFromExam,
  grade11BlankSchemeForTimestamp,
  isGrade11BlankScheme,
  resolveGrade11BlankScheme,
  toEpochMs,
} from '../constants/buraxilisConfig';

const mapExamDoc = (docSnap: { id: string; data: () => any }): Exam => {
  const data = docSnap.data();
  const createdAt = data.createdAt ?? null;
  // Missing/corrupt timestamp → 'new' (never leave undefined for buraxilis consumers)
  const grade11BlankScheme = isGrade11BlankScheme(data.grade11BlankScheme)
    ? data.grade11BlankScheme
    : resolveGrade11BlankScheme({ asOf: createdAt });

  return {
    id: docSnap.id,
    name: data.name || data.examName || 'Unknown Exam',
    type: data.type || 'Blok',
    collection: data.collection || 'scanned2610',
    examName: data.examName,
    archived: data.archived || false,
    archivedAt: data.archivedAt,
    createdAt,
    grade11BlankScheme,
  };
};

/**
 * If exam has no frozen scheme/createdAt, infer from student upload timestamps
 * and persist grade11BlankScheme (+ createdAt if missing) so later opens stay stable.
 */
export const enrichExamBlankScheme = async (exam: Exam): Promise<Exam> => {
  if (exam.type !== 'buraxilis') {
    return { ...exam, grade11BlankScheme: exam.grade11BlankScheme || 'new' };
  }

  // Explicit frozen scheme wins (including previously persisted 'new' fallback)
  if (isGrade11BlankScheme(exam.grade11BlankScheme)) {
    return exam;
  }

  // Try createdAt first; invalid/missing → continue to student sample / 'new'
  const fromCreatedAt = toEpochMs(exam.createdAt);
  if (fromCreatedAt != null) {
    const scheme = resolveGrade11BlankScheme({ asOf: fromCreatedAt, exam });
    return { ...exam, grade11BlankScheme: scheme };
  }

  try {
    const sampleSnap = await getDocs(query(collection(db, exam.collection), limit(8)));
    let earliest: number | null = null;
    sampleSnap.forEach((d) => {
      const data = d.data();
      const ms = toEpochMs(data.uploaded_at) ?? toEpochMs(data.scanned_at);
      if (ms != null && (earliest == null || ms < earliest)) earliest = ms;
    });

    // No usable upload time → 'new' (user requirement)
    const scheme = earliest != null
      ? resolveGrade11BlankScheme({ asOf: earliest })
      : 'new';
    const inferredCreatedAt =
      earliest != null ? Timestamp.fromMillis(earliest) : undefined;

    try {
      const patch: { grade11BlankScheme: Grade11BlankScheme; createdAt?: Timestamp } = {
        grade11BlankScheme: scheme,
      };
      if (inferredCreatedAt) patch.createdAt = inferredCreatedAt;
      await updateDoc(doc(db, 'exams', exam.id), patch);
    } catch (persistErr) {
      console.warn('⚠️ examService: Could not persist grade11BlankScheme:', persistErr);
    }

    return {
      ...exam,
      grade11BlankScheme: scheme,
      ...(inferredCreatedAt ? { createdAt: inferredCreatedAt } : {}),
    };
  } catch (err) {
    console.warn('⚠️ examService: enrichExamBlankScheme failed:', err);
    return { ...exam, grade11BlankScheme: 'new' };
  }
};

/**
 * Fetch all available exams from the 'exams' collection
 */
export const getAllExams = async (): Promise<Exam[]> => {
  try {
    console.log('📚 examService: Fetching all exams...');
    
    const examsRef = collection(db, 'exams');
    const querySnapshot = await getDocs(examsRef);
    
    const exams: Exam[] = [];
    
    querySnapshot.forEach((docSnap) => {
      exams.push(mapExamDoc(docSnap));
    });
    
    console.log('✅ examService: Exams fetched:', exams.length);
    return exams;
  } catch (error) {
    console.error('❌ examService: Error fetching exams:', error);
    throw error;
  }
};

/**
 * Get a specific exam by ID
 */
export const getExamById = async (examId: string): Promise<Exam | null> => {
  try {
    console.log('🔍 examService: Fetching exam:', examId);
    
    const examRef = doc(db, 'exams', examId);
    const examSnap = await getDoc(examRef);
    
    if (!examSnap.exists()) {
      console.log('❌ examService: Exam not found:', examId);
      return null;
    }
    
    const exam = mapExamDoc(examSnap);
    const enriched = await enrichExamBlankScheme(exam);
    
    console.log('✅ examService: Exam fetched:', enriched);
    return enriched;
  } catch (error) {
    console.error('❌ examService: Error fetching exam:', error);
    throw error;
  }
};

/**
 * Toggle exam archived status
 * @param examId - Exam document ID
 * @param archived - New archived status (true/false)
 * @returns Object with success status and message
 */
export const toggleExamArchive = async (
  examId: string,
  archived: boolean
): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(`🔄 examService: ${archived ? 'Archiving' : 'Unarchiving'} exam:`, examId);

    const examRef = doc(db, 'exams', examId);
    const updateData = {
      archived,
      ...(archived ? { archivedAt: Timestamp.now() } : {}),
      updatedAt: Timestamp.now()
    };

    await updateDoc(examRef, updateData);

    console.log(`✅ examService: Exam ${archived ? 'archived' : 'unarchived'} successfully`);
    return {
      success: true,
      message: `İmtahan uğurla ${archived ? 'arxivləşdi' : 'arxivdən çıxarıldı'}`
    };
  } catch (error) {
    console.error('❌ examService: Error toggling exam archive:', error);
    return {
      success: false,
      message: `Xəta: ${error}`
    };
  }
};

/**
 * Get exam by collection name
 * @param collectionName - Collection name to search for
 * @returns Exam object or null
 */
export const getExamByCollection = async (collectionName: string): Promise<Exam | null> => {
  try {
    console.log('🔍 examService: Fetching exam by collection:', collectionName);

    const examsRef = collection(db, 'exams');
    const q = query(examsRef, where('collection', '==', collectionName));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log('❌ examService: Exam not found for collection:', collectionName);
      return null;
    }

    const docSnap = querySnapshot.docs[0];
    const exam = await enrichExamBlankScheme(mapExamDoc(docSnap));

    console.log('✅ examService: Exam found:', exam);
    return exam;
  } catch (error) {
    console.error('❌ examService: Error fetching exam by collection:', error);
    return null;
  }
};

/**
 * Store selected exam in localStorage for session persistence
 */
export const setSelectedExam = (exam: Exam): void => {
  // Always freeze a concrete scheme; missing/bad timestamp → 'new'
  const normalized: Exam = {
    ...exam,
    grade11BlankScheme: isGrade11BlankScheme(exam.grade11BlankScheme)
      ? exam.grade11BlankScheme
      : resolveGrade11BlankScheme({ exam }),
  };
  localStorage.setItem('selectedExam', JSON.stringify(normalized));
  console.log('💾 examService: Selected exam stored:', normalized);
};

/**
 * Get selected exam from localStorage
 */
export const getSelectedExam = (): Exam | null => {
  const stored = localStorage.getItem('selectedExam');
  if (!stored) return null;

  try {
    const exam = JSON.parse(stored) as Exam;
    if (!isGrade11BlankScheme(exam.grade11BlankScheme)) {
      exam.grade11BlankScheme = resolveGrade11BlankScheme({ exam });
    }
    return exam;
  } catch (error) {
    console.error('❌ examService: Error parsing stored exam:', error);
    return null;
  }
};

/**
 * Clear selected exam from localStorage
 */
export const clearSelectedExam = (): void => {
  localStorage.removeItem('selectedExam');
  console.log('🗑️ examService: Selected exam cleared');
};

/**
 * Create a new exam in the 'exams' collection
 * @param collectionName - Collection name (e.g., 'blok2', 'scanned2610')
 * @param name - Display name for the exam (e.g., 'Blok İmtahanı - 2')
 * @param type - Exam type: 'Blok' or 'buraxilis'
 * @returns Object with success status, message, and examId
 */
export const createExam = async (
  collectionName: string,
  name: string,
  type: 'Blok' | 'buraxilis'
): Promise<{ success: boolean; message: string; examId?: string }> => {
  try {
    console.log('➕ examService: Creating exam:', { collectionName, name, type });

    // Validate inputs
    if (!collectionName || !name || !type) {
      return { success: false, message: 'Bütün xanaları doldurun' };
    }

    // Check for duplicate collection name
    const examsRef = collection(db, 'exams');
    const q = query(examsRef, where('collection', '==', collectionName));
    const existing = await getDocs(q);

    if (!existing.empty) {
      return { success: false, message: 'Bu collection adı artıq mövcuddur' };
    }

    const createdAt = Timestamp.now();
    const grade11BlankScheme = grade11BlankSchemeForTimestamp(createdAt);

    // Create exam document — scheme frozen at create time (Sept 2026 cutoff)
    const docRef = await addDoc(examsRef, {
      collection: collectionName,
      name,
      examName: name,
      type,
      createdAt,
      grade11BlankScheme,
    });

    console.log('✅ examService: Exam created:', docRef.id, 'grade11BlankScheme:', grade11BlankScheme);
    return {
      success: true,
      message: 'İmtahan uğurla yaradıldı',
      examId: docRef.id
    };
  } catch (error) {
    console.error('❌ examService: Error creating exam:', error);
    return { success: false, message: `Xəta: ${error}` };
  }
};

/**
 * Get questions for buraxilis exam by grade.
 * Pass selected exam (or scheme) so grade 11 uses old vs new blank automatically.
 */
export const getBuraxilisQuestionsByGrade = (
  grade: number,
  fenn: string,
  ctx?: BuraxilisBlankContext | Grade11BlankScheme | Exam | null
): string[] => {
  if (ctx && typeof ctx === 'object' && 'collection' in ctx) {
    return getBuraxilisQuestionsForFenn(grade, fenn, blankCtxFromExam(ctx as Exam));
  }
  return getBuraxilisQuestionsForFenn(
    grade,
    fenn,
    ctx as BuraxilisBlankContext | Grade11BlankScheme | null | undefined
  );
};
